import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Globe, Plus, Edit2, Trash2, Check, X } from 'lucide-react';
import axios from '../../lib/axios';
import { toast } from 'sonner';
import { getErrorMessage } from '../../utils/errorHelpers';

const DomainManagement = () => {
  const navigate = useNavigate();
  const [mappings, setMappings] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingMapping, setEditingMapping] = useState(null);
  const [formData, setFormData] = useState({
    tenant_id: '',
    domain: '',
    subdomain: '',
    tenant_code: '',
    ssl_enabled: true,
    active: true
  });

  useEffect(() => {
    fetchMappings();
    fetchTenants();
  }, []);

  const fetchMappings = async () => {
    try {
      const response = await axios.get('/api/admin/domain-mappings/all');
      setMappings(response.data.mappings);
    } catch (error) {
      console.error('Error fetching mappings:', error);
      toast.error(getErrorMessage(error, 'Failed to load domain mappings'));
    } finally {
      setLoading(false);
    }
  };

  const fetchTenants = async () => {
    try {
      const response = await axios.get('/api/admin/tenants');
      setTenants(response.data);
    } catch (error) {
      console.error('Error fetching tenants:', error);
    }
  };

  const handleCreate = async () => {
    try {
      if (!formData.tenant_id) {
        toast.error('Please select a tenant');
        return;
      }

      if (!formData.domain && !formData.subdomain && !formData.tenant_code) {
        toast.error('Please provide at least one: domain, subdomain, or tenant code');
        return;
      }

      await axios.post(
        `/api/admin/domain-mappings?tenant_id=${formData.tenant_id}`,
        formData
      );
      
      toast.success('Domain mapping created successfully');
      setShowAddModal(false);
      resetForm();
      fetchMappings();
    } catch (error) {
      console.error('Error creating mapping:', error);
      const errorMsg = getErrorMessage(error, 'Failed to create mapping');
      toast.error(errorMsg);
    }
  };

  const handleUpdate = async () => {
    try {
      await axios.put(
        `/api/admin/domain-mappings/${editingMapping._id}`,
        formData
      );
      
      toast.success('Domain mapping updated successfully');
      setEditingMapping(null);
      resetForm();
      fetchMappings();
    } catch (error) {
      console.error('Error updating mapping:', error);
      const errorMsg = getErrorMessage(error, 'Failed to update mapping');
      toast.error(errorMsg);
    }
  };

  const handleDelete = async (mappingId) => {
    if (!window.confirm('Are you sure you want to delete this domain mapping?')) {
      return;
    }

    try {
      await axios.delete(`/api/admin/domain-mappings/${mappingId}`);
      toast.success('Domain mapping deleted successfully');
      fetchMappings();
    } catch (error) {
      console.error('Error deleting mapping:', error);
      toast.error(getErrorMessage(error, 'Failed to delete mapping'));
    }
  };

  const handleEdit = (mapping) => {
    setEditingMapping(mapping);
    setFormData({
      tenant_id: mapping.tenant_id,
      domain: mapping.domain || '',
      subdomain: mapping.subdomain || '',
      tenant_code: mapping.tenant_code || '',
      ssl_enabled: mapping.ssl_enabled,
      active: mapping.active
    });
    setShowAddModal(true);
  };

  const resetForm = () => {
    setFormData({
      tenant_id: '',
      domain: '',
      subdomain: '',
      tenant_code: '',
      ssl_enabled: true,
      active: true
    });
    setEditingMapping(null);
  };

  const getTenantName = (tenantId) => {
    const tenant = tenants.find(t => t.id === tenantId);
    return tenant?.name || tenantId;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading domain mappings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/admin')}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-gray-600" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Domain Management</h1>
                <p className="text-sm text-gray-600">Configure custom domains and subdomains</p>
              </div>
            </div>
            <button
              onClick={() => setShowAddModal(true)}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Domain Mapping
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Domain Mappings Table */}
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tenant</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Domain</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Subdomain</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tenant Code</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">SSL</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {mappings.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center text-gray-500">
                    No domain mappings found. Click "Add Domain Mapping" to create one.
                  </td>
                </tr>
              ) : (
                mappings.map((mapping) => (
                  <tr key={mapping._id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {getTenantName(mapping.tenant_id)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{mapping.domain || '-'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{mapping.subdomain || '-'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{mapping.tenant_code || '-'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {mapping.ssl_enabled ? (
                        <Check className="w-5 h-5 text-green-500" />
                      ) : (
                        <X className="w-5 h-5 text-red-500" />
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {mapping.active ? (
                        <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800">
                          Active
                        </span>
                      ) : (
                        <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-800">
                          Inactive
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <button
                        onClick={() => handleEdit(mapping)}
                        className="text-blue-600 hover:text-blue-800 mr-3"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(mapping._id)}
                        className="text-red-600 hover:text-red-800"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Info Box */}
        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start">
            <Globe className="w-5 h-5 text-blue-600 mr-3 mt-0.5" />
            <div className="text-sm text-blue-800">
              <p className="font-semibold mb-1">How Domain Mapping Works:</p>
              <ul className="list-disc list-inside space-y-1">
                <li><strong>Custom Domain:</strong> legal.icicibank.com → ICICI Bank tenant</li>
                <li><strong>Subdomain:</strong> icici.legalopinion.ai → ICICI Bank tenant</li>
                <li><strong>Tenant Code:</strong> /t/icici → ICICI Bank tenant</li>
              </ul>
              <p className="mt-2 text-xs">
                Configure DNS CNAME records to point custom domains to your application URL.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Add/Edit Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h2 className="text-xl font-bold mb-4">
              {editingMapping ? 'Edit Domain Mapping' : 'Add Domain Mapping'}
            </h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tenant *
                </label>
                <select
                  value={formData.tenant_id}
                  onChange={(e) => setFormData({ ...formData, tenant_id: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  disabled={!!editingMapping}
                >
                  <option value="">Select tenant...</option>
                  {tenants.map((tenant) => (
                    <option key={tenant.id} value={tenant.id}>
                      {tenant.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Custom Domain
                </label>
                <input
                  type="text"
                  value={formData.domain}
                  onChange={(e) => setFormData({ ...formData, domain: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="legal.icicibank.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Subdomain
                </label>
                <input
                  type="text"
                  value={formData.subdomain}
                  onChange={(e) => setFormData({ ...formData, subdomain: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="icici"
                />
                <p className="text-xs text-gray-500 mt-1">Will be: icici.yourdomain.com</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tenant Code
                </label>
                <input
                  type="text"
                  value={formData.tenant_code}
                  onChange={(e) => setFormData({ ...formData, tenant_code: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="icici"
                />
                <p className="text-xs text-gray-500 mt-1">Will be: /t/icici/login</p>
              </div>

              <div className="flex items-center space-x-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.ssl_enabled}
                    onChange={(e) => setFormData({ ...formData, ssl_enabled: e.target.checked })}
                    className="mr-2"
                  />
                  <span className="text-sm">SSL Enabled</span>
                </label>

                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.active}
                    onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                    className="mr-2"
                  />
                  <span className="text-sm">Active</span>
                </label>
              </div>
            </div>

            <div className="flex space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowAddModal(false);
                  resetForm();
                }}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={editingMapping ? handleUpdate : handleCreate}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                {editingMapping ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DomainManagement;
